/**
 * KUOTA PO — batas jumlah pesanan untuk produk preorder yang jumlah barangnya
 * SUDAH diketahui (mig 3970).
 *
 * Dua skenario PO yang harus hidup berdampingan:
 *  - **Jumlah belum diketahui** → `products.po_limited = false`. Stok dikosongkan
 *    dan produk bebas dipesan; inilah perilaku PO sejak awal dan ia TIDAK diubah.
 *  - **Jumlah sudah pasti** → `po_limited = true` + `product_variants.po_quota`
 *    per varian. Pesanan berhenti di angka itu.
 *
 * KENAPA `limited` SELALU JADI SUMBU PERTAMA. Kuota kosong TIDAK bisa dipakai
 * sebagai penanda "tak terbatas": pada produk PO, kolom varian yang belum diisi
 * adalah keadaan NORMAL (mig 3630 mengukur 45.570 dari 47.139 baris varian
 * preorder inert di PROD), jadi angka kosong berarti *sengaja bebas* SEKALIGUS
 * *belum sempat diisi*. Toggle-lah yang merekam niat; angkanya hanya detail —
 * cermin `dp_required` terhadap `dp_amount` (`src/lib/dp.ts`).
 *
 * Konsekuensinya: mematikan toggle TIDAK menghapus angka yang sudah diketik
 * (gelombang PO berikutnya sering memakai angka yang sama), dan seluruh helper
 * di sini mengabaikan angka itu saat `limited=false`.
 *
 * Kuota BUKAN stok. `product_variants.stock` besaran fisik gudang yang punya
 * kartu stok, opname, kasir, dan retur di belakangnya; barang PO belum berwujud.
 * Lihat header mig 3970.
 */

/** Sisa kuota masih "banyak" di atas ambang ini — nadanya tenang, bukan mendesak. */
export const PO_QUOTA_LOW_PCS = 10;
/** …atau di atas porsi kuota ini. Yang lebih dulu tercapai yang menang. */
export const PO_QUOTA_LOW_RATIO = 0.2;

/**
 * Sisa pcs yang masih boleh dipesan. `null` = tak terbatas (toggle mati, atau
 * varian tanpa angka).
 *
 * Sengaja clamp ke 0: pesanan admin yang menembus kuota lewat konfirmasi
 * eksplisit membuat terpakai > kuota, dan "sisa -3 pcs" bukan kalimat yang
 * berguna bagi siapa pun.
 */
export function quotaRemaining(
  limited: boolean | null | undefined,
  quota: number | null | undefined,
  used: number | null | undefined
): number | null {
  if (!limited) return null;
  if (quota == null || !Number.isFinite(quota)) return null;
  return Math.max(quota - Math.max(used ?? 0, 0), 0);
}

/** Varian sudah tidak bisa dipesan karena jatahnya habis. */
export function isQuotaExhausted(
  limited: boolean | null | undefined,
  quota: number | null | undefined,
  used: number | null | undefined
): boolean {
  return quotaRemaining(limited, quota, used) === 0;
}

/**
 * Sisa kuota sudah menipis — kini dipakai memilih NADA baris kuota di halaman
 * produk, bukan lagi memutuskan apakah angkanya muncul sama sekali.
 *
 * Sampai 2026-09-03 fungsi inilah gerbang tampil/tidaknya angka: sisa sengaja
 * disembunyikan selama masih banyak karena "sisa 198 dari 200" dikhawatirkan
 * membaca sebagai produk sepi peminat. Pemilik membalik keputusan itu — produk
 * ready stock sudah selalu menyebut "Stok tersedia: N pcs", dan PO berkuota yang
 * bungkam sampai tinggal 20 pcs membuat pembeli tak punya cara tahu jatahnya
 * terbatas sampai jatah itu hampir habis. Sekarang angkanya selalu tampil;
 * `isQuotaLow` tinggal menaikkan urgensinya (hijau → kuning).
 *
 * Kuota yang sudah habis bukan "menipis" — di situ yang tampil badge habis.
 */
export function isQuotaLow(
  limited: boolean | null | undefined,
  quota: number | null | undefined,
  used: number | null | undefined
): boolean {
  const remaining = quotaRemaining(limited, quota, used);
  if (remaining == null || remaining <= 0) return false;
  const threshold = Math.max(
    PO_QUOTA_LOW_PCS,
    Math.round((quota ?? 0) * PO_QUOTA_LOW_RATIO)
  );
  return remaining <= threshold;
}

/**
 * Nada baris kuota di halaman produk. `ok` tenang (masih banyak) · `low`
 * mendesak (menipis) · `out` tertutup (jatah habis).
 */
export type PoQuotaTone = "ok" | "low" | "out";

/** Baris kuota PO siap-render untuk halaman produk storefront. */
export type PoQuotaLine = {
  tone: PoQuotaTone;
  label: string;
  /** Sisa pcs yang jadi dasar label — dipakai tes & caller yang butuh angkanya. */
  remaining: number;
};

/**
 * Baris "berapa yang masih bisa dipesan" untuk halaman produk PO — CERMIN baris
 * "Stok tersedia: N pcs" milik produk ready stock, yang sudah tampil sejak awal.
 *
 * `null` = TIDAK ADA yang boleh ditampilkan, dan itu bukan kasus pinggiran:
 *  - toggle "Stok PO Terbatas" mati → angka `po_quota` yang tersimpan INERT
 *    (45.570 dari 47.139 baris varian preorder di PROD, mig 3630). Menampilkannya
 *    berarti memasang angka yang tak mengikat apa pun di halaman pembeli.
 *  - varian belum terpilih / belum termuat (mode malas `lazyVariants.ts`) →
 *    `quota` masih `undefined`. Senyap lebih baik daripada menebak.
 *
 * Angkanya SISA, bukan plafon: yang dicari mata pembeli adalah "berapa yang
 * masih boleh saya ambil", dan itu pula angka yang sudah membatasi
 * `QuantityStepper`. Plafon telanjang justru mode gagal yang tercatat di audit
 * 2026-08-24 (lihat catatan di kaki berkas ini).
 */
export function quotaStorefrontLine(
  limited: boolean | null | undefined,
  quota: number | null | undefined,
  used: number | null | undefined
): PoQuotaLine | null {
  const remaining = quotaRemaining(limited, quota, used);
  if (remaining == null) return null;
  if (remaining <= 0) {
    return { tone: "out", label: "Kuota PO varian ini sudah habis", remaining: 0 };
  }
  if (isQuotaLow(limited, quota, used)) {
    return {
      tone: "low",
      label: `Kuota PO menipis — sisa ${remaining} pcs`,
      remaining,
    };
  }
  return { tone: "ok", label: `Kuota PO tersedia: ${remaining} pcs`, remaining };
}

export type PoQuotaDraftVariant = {
  /** Label untuk pesan error — nama kombinasi apa adanya. */
  name: string;
  /** Isi kolom kuota apa adanya dari form (belum di-parse). */
  text: string;
};

/**
 * Validasi sebelum simpan produk — CERMIN `validateDpDraft` (`src/lib/dp.ts`).
 * Toggle menyala berarti admin mengaku tahu angkanya; membiarkan varian kosong
 * mengembalikan arti-ganda yang justru dihapus toggle ini. Pesannya menyebut
 * jalan keluarnya, sama seperti pesan DP, supaya admin tak terjebak di form.
 */
export function validatePoQuotaDraft(
  limited: boolean,
  variants: PoQuotaDraftVariant[]
): string | null {
  if (!limited) return null;
  if (variants.length === 0) return null;

  const empty = variants.filter((v) => !v.text.trim());
  if (empty.length > 0) {
    const names = empty.slice(0, 3).map((v) => v.name).join(", ");
    const more = empty.length > 3 ? ` (+${empty.length - 3} lainnya)` : "";
    return (
      `Kuota PO wajib diisi karena "Stok PO Terbatas" menyala — masih kosong di ` +
      `${names}${more}. Isi 0 bila varian itu tidak dijual, atau matikan ` +
      `"Stok PO Terbatas" bila jumlahnya belum diketahui.`
    );
  }

  const bad = variants.find((v) => {
    const n = Number(v.text.trim());
    return !Number.isFinite(n) || n < 0 || !Number.isInteger(n);
  });
  if (bad) {
    return `Kuota PO "${bad.name}" harus berupa bilangan bulat ≥ 0.`;
  }

  return null;
}

/**
 * Nilai kuota yang ditulis ke DB — CERMIN `resolveStockWrite` (`variantSave.ts`)
 * dan mengikuti kontrak yang sama: **`null` = JANGAN SENTUH**.
 *
 * Alasannya identik dengan stok: kolom ini bisa berubah di luar form (admin lain,
 * layar lain), dan menulis ulang seluruh varian dari isi form pada setiap Simpan
 * persis mode gagal yang menghapus 177 pcs stok di PROD 2026-08-14. Perbandingan
 * atas TEKS, bukan angka — admin yang mengetik ulang "05" lebih baik dianggap
 * mengubah daripada ditebak.
 */
export function resolvePoQuotaWrite(input: {
  text: string;
  baseline: string | undefined;
  limited: boolean;
}): number | null {
  // Toggle mati: angka tidak berlaku dan TIDAK dihapus — biarkan apa adanya.
  if (!input.limited) return null;

  const text = input.text.trim();
  if (input.baseline !== undefined && text === input.baseline.trim()) return null;
  if (!text) return null;

  const n = Number(text);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return null;
  return n;
}

/**
 * Error dari trigger `enforce_preorder_quota` (mig 3970)?
 *
 * Dipakai admin untuk memunculkan konfirmasi "Lampaui kuota" — cermin idiom
 * `p_allow_unpaid` ("izinkan kirim pcs belum lunas") pada Request Kirim: aksi
 * yang menembus aturan tetap MUNGKIN, tapi harus disengaja dan terlihat.
 * Komponen admin TIDAK memanggilnya langsung — lewat
 * `quotaOverrideConfirmMessage`, yang juga mengenali kuota promo (bughunt
 * G8-18; tripwire `adminQuotaOverrideLint.test.ts`).
 *
 * Dicocokkan atas teks pesan, bukan kode: SQLSTATE-nya `check_violation`
 * (23514) yang dipakai bersama guard stok ready stock, jadi kodenya sendiri tak
 * cukup membedakan.
 */
export function isPoQuotaError(message: string | null | undefined): boolean {
  return /Kuota PO varian/i.test(message ?? "");
}

/**
 * Penolakan kuota KAMPANYE promo (trigger `enforce_promo_quota`, mig 6020) —
 * prefiks `PROMO_QUOTA:<jenis>` adalah kode mesin. Sejak mig 7570 Edit &
 * Tambah Pesanan Manual Detail Produksi membawa jejak kampanye, jadi kuota
 * promo ikut berlaku di sana; admin menembusnya lewat pembungkus over-quota
 * yang sama dengan kuota PO (trigger meloloskan admin ber-`app.allow_over_quota`).
 */
export function isPromoQuotaError(message: string | null | undefined): boolean {
  return /^\s*PROMO_QUOTA:(total|per_customer)\b/i.test(message ?? "");
}

/** Kalimat manusia untuk penolakan kuota promo; `null` bila bukan itu. */
export function promoQuotaMessage(message: string | null | undefined): string | null {
  const m = /^\s*PROMO_QUOTA:(total|per_customer)\s+([\s\S]*)$/i.exec(message ?? "");
  if (!m) return null;
  const inti = m[2].trim();
  return m[1].toLowerCase() === "total"
    ? `Kuota promo habis — ${inti}.`
    : `Batas promo per pembeli tercapai — ${inti}.`;
}

/** Pertanyaan konfirmasi menembus kuota promo (admin). */
export function overPromoQuotaConfirmMessage(serverMessage: string): string {
  return (
    `${promoQuotaMessage(serverMessage) ?? serverMessage}\n\n` +
    "Lanjutkan dan lampaui kuota promo? Pesanan tetap memakai harga promo " +
    "dan tercatat memakai kuota kampanye ini."
  );
}

/**
 * Konfirmasi tembus kuota untuk penolakan yang MEMANG dari kuota (PO atau
 * promo); `null` = bukan penolakan kuota, galatnya diteruskan apa adanya.
 *
 * Satu-satunya pintu komponen admin: Tambah Produk & Ubah Pcs keranjang
 * membawa jejak kampanye, dan pencocok kuota PO saja (`/Kuota PO varian/`)
 * meloloskan `PROMO_QUOTA:` sebagai galat mentah tanpa tawaran tembus
 * (bughunt G8-18). Galat yang tetap ditampilkan diterjemahkan lewat
 * `promoQuotaMessage(msg) ?? msg`.
 */
export function quotaOverrideConfirmMessage(serverMessage: string): string | null {
  if (isPoQuotaError(serverMessage)) return overQuotaConfirmMessage(serverMessage);
  if (isPromoQuotaError(serverMessage)) return overPromoQuotaConfirmMessage(serverMessage);
  return null;
}

/** Pertanyaan konfirmasi untuk admin, memuat pesan aslinya dari server. */
export function overQuotaConfirmMessage(serverMessage: string): string {
  return (
    `${serverMessage}\n\n` +
    "Lanjutkan dan lampaui kuota PO? Pesanan tetap dibuat, tapi jumlah yang " +
    "dipesan akan melebihi jumlah barang yang direncanakan."
  );
}

// ─── Sisa yang TERLIHAT (audit 2026-08-24) ───────────────────────────────────
/**
 * Kuota adalah **PLAFON RENCANA, bukan sisa hidup**: tidak ada satu pun penulis
 * yang menurunkan `po_quota` saat pesanan masuk — mig 3970 sengaja menurunkan
 * "terpakai" dari Σ `order_items` tanpa counter, supaya tak ada penulis kedua
 * yang bisa dobel-hitung.
 *
 * Yang TIDAK ikut dipikirkan waktu itu: satu-satunya layar yang menampilkan
 * sisa adalah Detail PO. Di form produk admin membaca angka plafon TELANJANG,
 * menyimpulkan "stok tidak berkurang walau sudah dipesan", lalu menurunkan
 * kuotanya sendiri agar cocok dengan sisa fisik. Mesin kemudian mengurangi
 * "terpakai" SEKALI LAGI dari angka yang sudah dikurangi ⇒ pcs terjual
 * terpotong dua kali dan varian menutup jauh lebih awal. Terukur di PROD
 * 2026-08-24: ZINIRA "Coffee" kuota 109 − terpesan 24 = 85 pcs boleh dijual
 * padahal fisiknya masih 106; Sora "Soft Cream" sudah tertutup total dengan 4
 * pcs masih di gudang.
 *
 * Karena itu helper di bawah ini tak pernah memulangkan angka telanjang: plafon,
 * terpesan, dan sisa selalu disebut BERSAMA. Angka yang tidak bisa disalahartikan
 * sebagai stok hidup tidak akan mengundang admin menurunkannya sendiri.
 */

/** Isi kolom kuota → angka; `null` bila kosong atau tak sah. */
export function parseQuotaText(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return null;
  return n;
}

/**
 * Kuota yang sedang diketik sudah DI BAWAH pcs yang terlanjur masuk. Bukan
 * larangan — pesanan lama tetap sah — tapi efeknya varian langsung tertutup,
 * jadi harus terlihat merah sebelum Simpan.
 */
export function isQuotaBelowUsed(
  quotaText: string,
  used: number | undefined
): boolean {
  const quota = parseQuotaText(quotaText);
  if (quota == null || used == null) return false;
  return quota < used;
}

/**
 * Rincian satu varian untuk wizard admin: `"Kuota 21 pcs · terpesan 2 · sisa 19"`.
 * `used` belum termuat → `null`: senyap lebih baik daripada memamerkan "0 pcs"
 * yang keliru (idiom yang sudah dipakai `PoQuotaUsedHint` sejak mig 3970).
 */
export function quotaBreakdownLabel(
  quotaText: string,
  used: number | undefined
): string | null {
  if (used == null) return null;
  const quota = parseQuotaText(quotaText);
  if (quota == null) return `Sudah dipesan: ${used} pcs`;
  return `Kuota ${quota} pcs · terpesan ${used} · sisa ${Math.max(quota - used, 0)}`;
}

/**
 * Badge daftar varian di tab Stok. "sisa" disebut LEBIH DULU karena itulah yang
 * dicari mata saat memindai daftar — dan angka kuota tetap ikut supaya jelas
 * bahwa yang di form adalah plafon, bukan sisa yang harus diedit sendiri.
 */
export function quotaListBadgeLabel(
  quotaText: string,
  used: number | undefined
): string {
  const quota = parseQuotaText(quotaText);
  if (quota == null) return "Belum diisi";
  if (used == null) return `${quota} pcs`;
  return `sisa ${Math.max(quota - used, 0)} · kuota ${quota}`;
}

/** Pcs terpesan satu varian, untuk ringkasan mode kuota SERAGAM. */
export type QuotaUsageEntry = { name: string; used: number };

/**
 * Ringkasan mode kuota SERAGAM — satu angka berlaku untuk SEMUA varian,
 * sementara tiap varian punya "terpesan" sendiri. Yang mengikat adalah varian
 * paling laris: dialah yang pertama menutup, dan dialah yang menentukan apakah
 * angka seragam yang diketik admin sudah di bawah pesanan yang masuk.
 *
 * Sampai audit 2026-08-24 kartu ini TIDAK punya keterangan apa pun — admin
 * menetapkan satu angka untuk semua varian tanpa tahu satu pun sudah terpakai
 * berapa.
 */
export function summarizeUniformQuota(
  quotaText: string,
  entries: QuotaUsageEntry[]
): {
  totalUsed: number;
  peak: QuotaUsageEntry | null;
  below: boolean;
  label: string | null;
} {
  if (entries.length === 0) {
    return { totalUsed: 0, peak: null, below: false, label: null };
  }

  const totalUsed = entries.reduce((sum, e) => sum + Math.max(e.used, 0), 0);
  const peak = entries.reduce<QuotaUsageEntry | null>(
    (best, e) => (e.used > 0 && (best == null || e.used > best.used) ? e : best),
    null
  );

  if (peak == null) {
    return {
      totalUsed: 0,
      peak: null,
      below: false,
      label: "Belum ada pesanan masuk untuk varian mana pun.",
    };
  }

  const quota = parseQuotaText(quotaText);
  const tail =
    quota == null
      ? ""
      : ` → sisa terkecil ${Math.max(quota - peak.used, 0)} pcs`;
  return {
    totalUsed,
    peak,
    below: quota != null && quota < peak.used,
    label: `Terpesan total ${totalUsed} pcs · terbanyak "${peak.name}" ${peak.used} pcs${tail}`,
  };
}

/** Baris kuota per varian (hasil `fetchPreorderQuotaRows`) untuk keranjang. */
export type QuotaByVariant = Map<string, { quota: number | null; used: number }>;

/**
 * Sisa pcs satu baris keranjang. `null` = tak terbatas ATAU tak diketahui —
 * keduanya diperlakukan sama dan PERMISIF, mengikuti doktrin mig 3970: gerbang
 * otoritatifnya trigger DB, jadi peta yang gagal dimuat paling jauh membuat
 * pembeli ditolak saat checkout — jauh lebih baik daripada mengunci keranjang
 * yang sebenarnya masih sah karena jaringan sedang buruk.
 */
export function cartLineRemaining(
  row: { quota: number | null; used: number } | undefined
): number | null {
  if (!row || row.quota == null) return null;
  return Math.max(row.quota - Math.max(row.used, 0), 0);
}

/**
 * Baris keranjang yang qty-nya sudah melewati sisa kuota. Dipakai keranjang
 * untuk menahan checkout SEBELUM ditembak ke DB: sampai audit 2026-08-24
 * pembeli baru tahu kuotanya habis setelah menekan CHECKOUT, karena keranjang
 * (localStorage) bisa menyimpan qty yang disusun jauh sebelum varian itu penuh.
 */
export function overQuotaCartLines<
  T extends { variationId: string; variationName: string; qty: number },
>(lines: T[], quotaByVariant: QuotaByVariant): { line: T; remaining: number }[] {
  const out: { line: T; remaining: number }[] = [];
  for (const line of lines) {
    const remaining = cartLineRemaining(quotaByVariant.get(line.variationId));
    if (remaining != null && line.qty > remaining) out.push({ line, remaining });
  }
  return out;
}
